import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Typography,
  message,
} from "antd"
import { SearchOutlined, SwapOutlined } from "@ant-design/icons"
import { doc, getDoc } from "firebase/firestore"
import { useEffect, useState } from "react"
import axiosInstance from "../../../../axios/axiosConfig"
import { db } from "../../../../firebase/config"

const { Title, Text, Link } = Typography
const { TabPane } = Tabs

const blankKnife = {
  id: "none",
  code: "N/A",
  width: 0,
  height: 0,
  rowss: 0,
}

// This can be moved to a separate utils/transformers.js file
const transformKnifeData = (k) => ({
  id: k.id.toString(),
  code: k.code,
  width: +k.width,
  height: +k.height,
  Radius: +k.Radius,
  TypeOfForm: k.TypeOfForm,
  SHAPE: k.SHAPE,
  rowss: +k.rowss,
  spaceBetweenRows: +k.spaceBetweenRows,
  Form_Rows: +k.Form_Rows,
  gapRaport: +k.gapRaport,
  Z: +k.Z,
  Z_raports: +k.Z_raports,
  Rotary_Or_SemiRotary: k.Rotary_Or_SemiRotary === "rotary",
  Knives_WIDTH_mm: +k.Knives_WIDTH_mm,
  // Keep adding other properties as needed
  Cutter_Knives_Width_until_313mm: k.Cutter_Knives_Width_until_313mm,
  Arsoma_knife_width_until_275mm_Z: k.Arsoma_knife_width_until_275mm_Z,
  EM_340_knife_width_until_325_Z: k.EM_340_knife_width_until_325_Z,
  Hot_Foil: k.Hot_Foil,
})

export default function KnifePopup({
  knifeCode,
  knifeId,
  selectedMachine,
  setOrderData,
  setUpdatedKnife,
}) {
  // --- STATE MANAGEMENT ---
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [knifeLoading, setKnifeLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)

  const [currentKnifeData, setCurrentKnifeData] = useState(blankKnife)
  const [previousKnife, setPreviousKnife] = useState(blankKnife)
  const [showPrevious, setShowPrevious] = useState(false)

  // Search inputs
  const [knifeCodeSearch, setKnifeCodeSearch] = useState("")
  const [minWidth, setMinWidth] = useState(null)
  const [minHeight, setMinHeight] = useState(null)
  const [knifeRange, setKnifeRange] = useState(0) // Default range to 0

  const [knifeSearchResults, setKnifeSearchResults] = useState([])

  // --- DATA FETCHING & SIDE EFFECTS ---

  // Load current knife details when modal opens
  useEffect(() => {
    if (!isModalOpen) return

    setKnifeLoading(true)
    // Reset states on open
    setKnifeSearchResults([])
    setShowPrevious(false)

    getDoc(doc(db, "knives", knifeId))
      .then((snap) => {
        if (snap.exists()) {
          const knifeData = transformKnifeData({ ...snap.data(), id: snap.id })
          setCurrentKnifeData(knifeData)
          setPreviousKnife(knifeData)
        } else {
          message.warning("No knife found, please search for a new one.")
          setCurrentKnifeData(blankKnife)
          setPreviousKnife(blankKnife)
        }
      })
      .catch(() => message.error("Failed to load current knife data."))
      .finally(() => setKnifeLoading(false))
  }, [isModalOpen, knifeId])

  // --- API & EVENT HANDLERS ---

  const handleSearch = async (searchType) => {
    let params = {}
    if (searchType === "code") {
      if (!knifeCodeSearch) {
        message.warn("Please enter a knife code to search.")
        return
      }
      params = { code: knifeCodeSearch }
    } else {
      // 'size'
      if (minWidth == null || minHeight == null) {
        message.warn("Please enter both a minimum width and height.")
        return
      }
      params = { code: null, width: minWidth, height: minHeight, range: knifeRange, shape: "any" }
    }

    setSearchLoading(true)
    setKnifeSearchResults([]) // Clear previous results

    try {
      const { data } = await axiosInstance.get("/api/search", { params })

      const filterByMachine = (arr) => {
        switch (selectedMachine) {
          case "Arsoma":
            return arr.filter((k) => k.Arsoma_knife_width_until_275mm_Z)
          case "Viva":
            return arr.filter((k) => k.EM_340_knife_width_until_325_Z || k.Hot_Foil)
          case "Cutter":
            return arr.filter((k) => k.Cutter_Knives_Width_until_313mm)
          default:
            message.error("A machine must be selected to filter results.")
            return []
        }
      }

      const results = data.map(transformKnifeData)
      const filteredResults = filterByMachine(results)
      setKnifeSearchResults(results)

      if (filteredResults.length === 0) {
        message.info("No knives found matching your criteria.")
      }
    } catch (error) {
      message.error("An error occurred during the search.")
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSwap = (knife) => {
    setCurrentKnifeData(knife)
    setShowPrevious(true)
    message.success(`Knife ${knife.code} selected.`)
  }

  const handleOk = () => {
    setOrderData((prev) => ({
      ...prev,
      selectedProduct: {
        ...prev.selectedProduct,
        knifeId: currentKnifeData.id,
        knifeCode: currentKnifeData.code,
        labelWidth: currentKnifeData.width,
        labelHeight: currentKnifeData.height,
        numberOfColumns: currentKnifeData.rowss,
      },
    }))

    if (previousKnife.id !== currentKnifeData.id) {
      setUpdatedKnife(true)
    }
    setIsModalOpen(false)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  // --- UI DEFINITION ---

  const columns = [
    { title: "Code", dataIndex: "code", key: "code" },
    { title: "Width", dataIndex: "width", sorter: (a, b) => a.width - b.width },
    { title: "Height", dataIndex: "height", sorter: (a, b) => a.height - b.height },
    { title: "Rows", dataIndex: "rowss", sorter: (a, b) => a.rowss - b.rowss },
    { title: "Shape", dataIndex: "TypeOfForm" },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          ghost
          size="small"
          icon={<SwapOutlined />}
          onClick={() => handleSwap(record)}
          disabled={currentKnifeData.id === record.id}>
          Select
        </Button>
      ),
    },
  ]

  return (
    <>
      <Link onClick={() => setIsModalOpen(true)}>{knifeCode}</Link>
      <Modal
        title="Find & Swap Knife"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={1200}
        okText="Save & Update"
        okButtonProps={{ disabled: currentKnifeData.id === previousKnife.id }}
        destroyOnClose // Resets state inside modal when closed
      >
        {knifeLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Row gutter={24}>
            {/* Left Panel: Current Knife Details */}
            <Col span={7}>
              <Title level={5} style={{ marginTop: 0 }}>
                Current Knife
              </Title>
              <Card>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Code">
                    <Text strong copyable>
                      {currentKnifeData.code}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Dimensions">{`${currentKnifeData.width} x ${currentKnifeData.height} mm`}</Descriptions.Item>
                  <Descriptions.Item label="Rows">{currentKnifeData.rowss}</Descriptions.Item>
                  <Descriptions.Item label="Knife Width">
                    {currentKnifeData.Knives_WIDTH_mm} mm
                  </Descriptions.Item>
                  <Descriptions.Item label="Shape">{currentKnifeData.SHAPE}</Descriptions.Item>
                </Descriptions>
                {showPrevious && (
                  <Alert
                    style={{ marginTop: 16 }}
                    message={
                      <>
                        Originally: <Text strong>{previousKnife.code}</Text>
                      </>
                    }
                    type="info"
                    showIcon
                  />
                )}
              </Card>
            </Col>

            {/* Right Panel: Search & Results */}
            <Col span={17}>
              <Tabs defaultActiveKey="1" type="card">
                <TabPane tab="Search by Size" key="1">
                  <Space align="end" wrap>
                    <Space direction="vertical">
                      <Text>Min Width</Text>
                      <InputNumber
                        value={minWidth}
                        onChange={setMinWidth}
                        addonAfter="mm"
                        placeholder="e.g., 100"
                      />
                    </Space>
                    <Space direction="vertical">
                      <Text>Min Height</Text>
                      <InputNumber
                        value={minHeight}
                        onChange={setMinHeight}
                        addonAfter="mm"
                        placeholder="e.g., 50"
                      />
                    </Space>
                    <Space direction="vertical">
                      <Text>Range (+/-)</Text>
                      <InputNumber
                        value={knifeRange}
                        onChange={setKnifeRange}
                        addonAfter="mm"
                        placeholder="e.g., 2"
                      />
                    </Space>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={() => handleSearch("size")}
                      loading={searchLoading}
                      disabled={minWidth == null || minHeight == null}>
                      Search
                    </Button>
                  </Space>
                </TabPane>
                <TabPane tab="Search by Code" key="2">
                  <Space.Compact style={{ width: "100%" }}>
                    <Input
                      placeholder="Enter exact knife code"
                      value={knifeCodeSearch}
                      onChange={(e) => setKnifeCodeSearch(e.target.value.toUpperCase())}
                    />
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={() => handleSearch("code")}
                      loading={searchLoading}
                      disabled={!knifeCodeSearch}>
                      Find
                    </Button>
                  </Space.Compact>
                </TabPane>
              </Tabs>

              <Divider style={{ marginTop: 0 }} />

              <Table
                columns={columns}
                dataSource={knifeSearchResults}
                rowKey="id"
                loading={searchLoading}
                size="small"
                locale={{
                  emptyText: <Empty description="No knives found. Adjust your search criteria." />,
                }}
                pagination={{ pageSize: 5, hideOnSinglePage: true }}
              />
            </Col>
          </Row>
        )}
      </Modal>
    </>
  )
}
